import { supplierSkuQuery } from "@/api/listings";
import { listingsQuery, onboardingStateQuery, useHideMarketplaceMenuItems } from "@/api/supplier";
import { Permission, usePermissions } from "@/api/user";
import { queryClient } from "@/api/utils";
import { AppLayout } from "@/components/layout/AppLayout";
import {
  NavigationMenu,
  NavigationMenuHeader,
  NavigationMenuHeading,
  NavigationMenuLink,
  SupplierNavigationMenuItems,
} from "@/components/layout/NavigationMenu";
import { FloatingSupplierTodoList } from "@/components/supplier/FloatingSupplierTodoList";
import { useHasUnreads } from "@/hooks/useHasUnreads";
import { MoneyFnsProvider } from "@/modules/money-fns";
import { useIsUpdateAvailable } from "@/utils/check-for-updates";
import { checkAuth, checkSupplierRole } from "@/utils/router";
import {
  CalendarIcon,
  ChatBubbleTextSquareIcon,
  CogIcon,
  DollarCoinIcon,
  HealthcareIcon,
  HomeIcon,
  PageSettingIcon,
  ReceiptIcon,
  ReloadIcon,
  ScannerIcon,
  ShippingBoxIcon,
  TaskListIcon,
} from "@givenwell/core-solid-icons";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router";

export const Route = createFileRoute("/supplier/$supplierId")({
  loader: async ({ params }) => {
    await checkAuth();
    await checkSupplierRole(params.supplierId);

    queryClient.prefetchQuery(onboardingStateQuery(params.supplierId));
  },
  // Do not cache this route's data after it's unloaded
  gcTime: 0,
  // Only reload the route when the user navigates to it or when deps change
  shouldReload: false,
  component: Layout,
});

function Layout() {
  const { supplierId } = Route.useParams();

  const sidebar = useMatches({
    select(matches) {
      return matches?.findLast(match => match.context.sidebar)?.context.sidebar;
    },
  });

  return (
    <AppLayout sidebar={sidebar || <Sidebar />} role="supplier">
      <MoneyFnsProvider>
        <Outlet />
        <FloatingSupplierTodoList />
      </MoneyFnsProvider>
    </AppLayout>
  );
}

function Sidebar() {
  const { supplierId } = Route.useParams();
  const { data: sku } = useQuery(supplierSkuQuery(supplierId));
  const { data: listings } = useQuery(listingsQuery(supplierId, undefined));
  const hasPermission = usePermissions(supplierId);
  const hasQuantityTrackedSkus = (sku?.skus.length ?? 0) > 0;
  const hasUnreads = useHasUnreads(
    supplierId,
    20,
    hasPermission(Permission.Admin.ViewMemberData, Permission.Supplier.Default),
  );

  const hasEAPListings = listings?.some(listing => listing.is_eap) ?? false;
  const hideMarketplaceItems = useHideMarketplaceMenuItems(supplierId);

  const updateAvailable = useIsUpdateAvailable();

  return (
    <NavigationMenu header={<NavigationMenuHeader />} footer={<SupplierNavigationMenuItems />}>
      <NavigationMenuLink
        to="/supplier/$supplierId"
        params={{ supplierId }}
        icon={<HomeIcon />}
        activeOptions={{ exact: true }}
        reloadDocument={updateAvailable}
      >
        Dashboard
      </NavigationMenuLink>
      {hasPermission(Permission.Admin.ViewMemberData, Permission.Supplier.Default) && (
        <NavigationMenuLink
          to="/supplier/$supplierId/messages"
          params={{ supplierId }}
          icon={<ChatBubbleTextSquareIcon />}
          reloadDocument={updateAvailable}
          badge={hasUnreads}
        >
          Messages
        </NavigationMenuLink>
      )}
      <NavigationMenuLink
        to="/supplier/$supplierId/payments"
        params={{ supplierId }}
        icon={<DollarCoinIcon />}
        reloadDocument={updateAvailable}
      >
        Payments
      </NavigationMenuLink>
      <NavigationMenuHeading>Sales</NavigationMenuHeading>
      <NavigationMenuLink
        to="/supplier/$supplierId/sales"
        params={{ supplierId }}
        icon={<ReceiptIcon />}
        reloadDocument={updateAvailable}
      >
        Sales History
      </NavigationMenuLink>
      <NavigationMenuLink
        to="/supplier/$supplierId/bookings"
        params={{ supplierId }}
        icon={<CalendarIcon />}
        reloadDocument={updateAvailable}
      >
        Bookings
      </NavigationMenuLink>
      {!hideMarketplaceItems && (
        <>
          <NavigationMenuLink
            to="/supplier/$supplierId/subscriptions"
            params={{ supplierId }}
            icon={<ReloadIcon />}
            reloadDocument={updateAvailable}
          >
            Subscriptions
          </NavigationMenuLink>
          <NavigationMenuLink
            to="/supplier/$supplierId/passes"
            params={{ supplierId }}
            icon={<TaskListIcon />}
            reloadDocument={updateAvailable}
          >
            Passes
          </NavigationMenuLink>
          <NavigationMenuLink
            to="/supplier/$supplierId/orders"
            params={{ supplierId }}
            icon={<ShippingBoxIcon />}
            reloadDocument={updateAvailable}
          >
            Orders
          </NavigationMenuLink>
        </>
      )}
      <NavigationMenuHeading>Setup</NavigationMenuHeading>
      {hasEAPListings && (
        <NavigationMenuLink
          to="/supplier/$supplierId/eap"
          params={{ supplierId }}
          icon={<HealthcareIcon />}
          reloadDocument={updateAvailable}
        >
          EAP Listings
        </NavigationMenuLink>
      )}
      {!hideMarketplaceItems && (
        <NavigationMenuLink
          to="/supplier/$supplierId/listings"
          params={{ supplierId }}
          icon={<PageSettingIcon />}
          reloadDocument={updateAvailable}
        >
          {hasEAPListings ? "Marketplace Listings" : "Listings"}
        </NavigationMenuLink>
      )}
      {hasQuantityTrackedSkus && (
        <NavigationMenuLink
          to="/supplier/$supplierId/sku"
          params={{ supplierId }}
          icon={<ScannerIcon />}
          reloadDocument={updateAvailable}
        >
          SKU&nbsp;Management
        </NavigationMenuLink>
      )}
      <NavigationMenuLink
        to="/supplier/$supplierId/settings"
        params={{ supplierId }}
        icon={<CogIcon />}
        reloadDocument={updateAvailable}
      >
        Settings
      </NavigationMenuLink>
    </NavigationMenu>
  );
}
